import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { PageHeader, KpiCard, EmptyState } from "../components/Shared";
import { fmtDate, statusBadgeClass } from "../lib/format";
import { GraduationCap, ClipboardCheck, CheckCircle2, AlertTriangle, ArrowRight, RotateCcw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const STATUS_LABEL = { pending_ceo: "Pending CEO approval", active: "Active", submitted: "Submitted", validated: "Validated", rejected: "Rejected" };
const isGraded = (f) => !!(f.evaluation && Object.keys(f.evaluation).length) || f.evaluation_total != null;
const isPendingEval = (f) => f.status === "submitted" && !isGraded(f);

export default function SupervisorDashboard() {
  const navigate = useNavigate();
  const { data: forms = [] } = useQuery({
    queryKey: ["internship-forms"],
    queryFn: () => api.get("/internship-forms").then((r) => r.data),
    refetchInterval: 15000,
  });

  const pending = forms.filter(isPendingEval);
  const needsRework = forms.filter((f) => f.status === "submitted" && f.ceo_returned);
  const graded = forms.filter(isGraded);
  const pendingSorted = [...pending].sort((a, b) => {
    const rank = { submitted: 0, active: 1, validated: 2 };
    return (rank[a.status] ?? 9) - (rank[b.status] ?? 9);
  });

  return (
    <div data-testid="supervisor-dashboard">
      <PageHeader title="Supervisor Workspace" subtitle="Students awaiting your evaluation">
        <GraduationCap className="h-6 w-6 text-[#1E3A8A]" />
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KpiCard testId="ss-kpi-total" icon={GraduationCap} title="Total students" accent="text-slate-800">{forms.length}</KpiCard>
        <KpiCard testId="ss-kpi-pending" icon={ClipboardCheck} title="Pending evaluation" accent="text-amber-600">{pending.length}</KpiCard>
        <KpiCard testId="ss-kpi-rework" icon={RotateCcw} title="Needs rework" accent="text-orange-600">{needsRework.length}</KpiCard>
        <KpiCard testId="ss-kpi-graded" icon={CheckCircle2} title="Evaluated" accent="text-emerald-600">{graded.length}</KpiCard>
      </div>

      {needsRework.length > 0 && (
        <div className="rounded-xl border border-orange-200 bg-orange-50/40 shadow-sm overflow-hidden mb-6" data-testid="ss-rework-panel">
          <div className="flex items-center justify-between px-5 py-4 border-b border-orange-100">
            <div>
              <h2 className="text-lg font-semibold text-orange-800">Returned by CEO — needs rework</h2>
              <p className="text-sm text-orange-700/80">The CEO sent these back for corrections. Revise the evaluation and re-send, or return to the student.</p>
            </div>
          </div>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Ref</TableHead><TableHead>Student</TableHead><TableHead>School</TableHead><TableHead>CEO note</TableHead><TableHead className="text-right">Action</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {needsRework.map((f) => (
                <TableRow key={f.id} data-testid={`ss-rework-row-${f.id}`}>
                  <TableCell className="font-mono-num text-xs text-slate-500">{f.ref_no || "—"}</TableCell>
                  <TableCell className="font-medium text-slate-800">{f.student_name || "—"}</TableCell>
                  <TableCell className="text-slate-600">{f.school || "—"}</TableCell>
                  <TableCell className="text-slate-600 text-sm max-w-[220px] truncate" title={f.decision_note || ""}>{f.decision_note || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" data-testid={`ss-rework-open-${f.id}`} className="bg-orange-600 hover:bg-orange-700" onClick={() => navigate(`/students?evaluate=${f.id}`)}>Review</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="ss-pending-panel">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Pending evaluations</h2>
            <p className="text-sm text-slate-500">Submitted or active student forms you haven't graded yet.</p>
          </div>
          <Button variant="outline" data-testid="ss-open-students" onClick={() => navigate("/students")}>
            Open Students <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
        {pendingSorted.length === 0 ? (
          <EmptyState title="All caught up" description="There are no students waiting for evaluation right now." />
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Ref</TableHead><TableHead>Student</TableHead><TableHead>School</TableHead>
              <TableHead>Period</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {pendingSorted.map((f) => (
                <TableRow key={f.id} data-testid={`ss-pending-row-${f.id}`} className={f.overdue ? "bg-red-50/40" : ""}>
                  <TableCell className="font-mono-num text-xs text-slate-500">{f.ref_no || "—"}</TableCell>
                  <TableCell className="font-medium text-slate-800">{f.student_name || "—"}</TableCell>
                  <TableCell className="text-slate-600">{f.school || "—"}</TableCell>
                  <TableCell className="text-slate-600 text-sm">{fmtDate(f.period_start)} → {fmtDate(f.period_end)}</TableCell>
                  <TableCell><span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${statusBadgeClass(f.status)}`}>{STATUS_LABEL[f.status] || f.status}</span></TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" data-testid={`ss-evaluate-${f.id}`} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]" onClick={() => navigate(`/students?evaluate=${f.id}`)}>Evaluate</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
